import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { CTBReport, deriveAssetType } from "../../../utils/api/endpoints/reports/parseReports";
import useProgram from "../../../utils/hooks/programs/useProgram";
import { ProgramTarget } from "../../../utils/api/endpoints/programs/parsePrograms";
import RichTextRenderer from "../../common/RichTextRenderer";
import ReportStatusPill from "../ReportStatusPill";
import VulnerabilityFlag, {
  getVulnerabilityColour
} from "../VulnerabilityFlag";
import { CgProfile } from "react-icons/cg";
import FilterSectionReports from "../utils/FIlterSectionReports";
import { FilterOptions } from "../../../utils/api/endpoints/reports/reports";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";
import Pagination from "../../retests/utils/Pagination";
import ConfirmationModal from "../../common/ConfirmationModal";
import { REPORT_STATUS_INFO } from "../../../utils/constants/reportStatusInfo";
import { useUpdateReportStateMutation, useGetReportsQuery, useBulkArchiveReportsMutation } from "../../../utils/api/endpoints/reportsApi";
import { ReportState, isReportClosed } from "../../../utils/api/endpoints/reports/reports";
import { ReportRejectReason } from "../../../utils/api/endpoints/reports/parseReports";
import toast from "react-hot-toast";
import { FiChevronDown } from "react-icons/fi";
import BulkActIcon from "../../../assets/icons/bulk-act.svg";
import { createPortal } from "react-dom";
import { retestRequest } from "../../../utils/api/endpoints/retests/retests";
import BulkActionsBar from "../BulkActionsBar";
import { useDispatch } from "react-redux";
import { api } from "../../../utils/api/api";

// Asset type colors for random selection
const assetTypeColors = ['#155E75', '#92400E', '#1E40AF'];

// Function to get a random color for asset type
const getRandomAssetTypeColor = (text: string) => {
  // Use the text as a seed for consistent color per asset type
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = text.charCodeAt(i) + ((hash << 5) - hash);
  }
  return assetTypeColors[Math.abs(hash) % assetTypeColors.length];
};

// Types
type ReportStateType =
  | "approved"
  | "rejected"
  | "closed"
  | "request info"
  | "acceptRisk"
  | "nonActionableIssue"
  | "requestFix"
  | "out of scope"
  | "QA Modified";

type ActionKeyType =
  | "approved"
  | "rejected"
  | "closed"
  | "request_info"
  | "acceptRisk"
  | "nonActionableIssue"
  | "requestFix"
  | "retest_requested"
  | "archive"
  | "unarchive";

type ColumnVisibility = {
  createdAt: boolean;
  pentester: boolean;
  action: boolean;
  assetType: boolean;
};

// Global variable to track active dropdown
let activeDropdownId: number | null = null;

const ReportTable = ({
  reports,
  filterOptions,
  filters,
  onFilter,
  onClick,
  count,
  currentPage,
  onPageChange,
  onRefresh,
  hideFilters = false
}: {
  reports: CTBReport[];
  filterOptions: FilterOptions;
  filters: {
    searchQuery: string;
    program: string;
    reportState: string;
    severityCategory: string;
    vulnerabilityType: string;
    sortOrder: string;
    pentesterUsername: string;
    includeArchived?: boolean | string;
    includeClosed?: boolean;
  };
  onFilter: (filters: {
    searchQuery: string;
    program: string;
    reportState: string;
    severityCategory: string;
    vulnerabilityType: string;
    sortOrder: string;
    pentesterUsername: string;
    includeArchived?: boolean | string;
    includeClosed?: boolean;
  }) => void;
  onClick: (report: CTBReport) => void;
  count: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
  hideFilters?: boolean;
}) => {
  const { role } = useUserCredentials();
  const dispatch = useDispatch();
  const pageSize = 7;
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    description: "",
    onConfirm: () => { },
    info: undefined as string | undefined,
    warning: undefined as string | undefined
  });
  const [selectedReports, setSelectedReports] = useState<number[]>([]);
  const [bulkActionsEnabled, setBulkActionsEnabled] = useState<boolean>(false);
  const [updateReportState] = useUpdateReportStateMutation();
  const [bulkArchiveReports] = useBulkArchiveReportsMutation();

  // Query to get total actionable reports count for the bulk actions indicator
  const { data: actionableCountData } = useGetReportsQuery(
    {
      search: filters.searchQuery,
      limit: 1, // We only need the count, not the actual reports
      page: 1,
      // Convert the filters to the format expected by the API
      severity: filters.severityCategory ? [filters.severityCategory] : undefined,
      reportState: 'Business Approved,Awaiting Fix', // Only actionable statuses
      type: filters.vulnerabilityType || undefined
    },
    {
      skip: !bulkActionsEnabled || !role || ![UserRole.BUSINESS, UserRole.BUSINESS_MANAGER, UserRole.BUSINESS_ADMINISTRATOR, UserRole.DEVELOPER, UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER].includes(role)
    }
  );

  const totalActionableCount = actionableCountData?.results || 0;
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(
    () => {
      if (role === UserRole.RESEARCHER) {
        return {
          createdAt: true,
          pentester: false,
          action: false,
          assetType: false
        };
      }
      return {
        createdAt: true,
        pentester: false,
        action: true,
        assetType: false
      };
    }
  );

  // Update column visibility when role changes
  useEffect(() => {
    if (role === UserRole.RESEARCHER) {
      setColumnVisibility({
        createdAt: true,
        pentester: false,
        action: false,
        assetType: false
      });
    } else {
      setColumnVisibility({
        createdAt: true,
        pentester: false,
        action: true,
        assetType: false
      });
    }
  }, [role]);

  // Clear selected reports when bulk actions are disabled
  useEffect(() => {
    if (!bulkActionsEnabled) {
      setSelectedReports([]);
    }
  }, [bulkActionsEnabled]);

  const handleReportClick = (report: CTBReport) => {
    localStorage.setItem("lastViewedReportId", report.id.toString());
    onClick(report);
  };

  // Handle report selection
  const handleReportSelection = (reportId: number, isSelected: boolean) => {
    if (isSelected) {
      setSelectedReports(prev => [...prev, reportId]);
    } else {
      setSelectedReports(prev => prev.filter(id => id !== reportId));
    }
  };

  // Handle bulk selection change (for BulkActionsBar compatibility)
  const handleBulkSelectionChange = (selectedIds: number[]) => {
    setSelectedReports(selectedIds);
  };

  const totalPages = Math.ceil(count / pageSize);

  // Helper to show confirmation modal
  const showConfirmation = (
    title: string,
    description: string,
    onConfirm: () => void,
    info?: string,
    warning?: string
  ) => {
    setModalConfig({
      isOpen: true,
      title,
      description,
      onConfirm,
      info,
      warning
    });
  };

  // Helper to close modal
  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  // Helper to get available actions for a report based on role/state
  const getAvailableActions = (
    report: CTBReport,
    role: UserRole
  ): { key: ActionKeyType; label: string; isRetestRequest?: boolean }[] => {
    const actions: {
      key: ActionKeyType;
      label: string;
      isRetestRequest?: boolean;
    }[] = [];

    // If report is closed, don't show any actions
    if (report.state === ReportState.CLOSED) {
      return actions; // Return empty actions array
    }

    if (
      [
        UserRole.ADMIN,
        UserRole.SUB_ADMIN,
        UserRole.ADMIN_MANAGER,
        UserRole.QA
      ].includes(role)
    ) {
      if (
        report.triageStatus === "underReview" ||
        report.state === ReportState.TRIAGE_REJECTED ||
        report.state === ReportState.TRIAGE_APPROVED
      ) {
        actions.push({ key: "approved", label: "Approve" });
        actions.push({ key: "rejected", label: "Reject" });
      }
    }

    if (
      [
        UserRole.BUSINESS,
        UserRole.DEVELOPER,
        UserRole.BUSINESS_MANAGER,
        UserRole.BUSINESS_ADMINISTRATOR
      ].includes(role)
    ) {
      // If there's an active retest status, don't show any business actions
      // EXCEPT when retest status is "Fix Verified" - then allow Close action
      if (report.retestStatus && report.retestStatus !== "Fix Verified") {
        return actions; // Return empty actions array
      }

      // Special case: If retest status is "Fix Verified", only allow Close action
      if (report.retestStatus === "Fix Verified") {
        actions.push({ key: "closed", label: "Close" });
        return actions;
      }

      if (
        report.state === ReportState.TRIAGE_APPROVED ||
        report.state === ReportState.BUSINESS_REVIEW
      ) {
        actions.push({ key: "approved", label: "Approve" });
        actions.push({ key: "request_info", label: "Request More Info" });
        actions.push({ key: "acceptRisk", label: "Accept Risk" });
        actions.push({
          key: "nonActionableIssue",
          label: "Non-Actionable Issue"
        });
      }
      if (
        report.state &&
        [
          ReportState.TRIAGE_APPROVED,
          ReportState.FIX_APPROVED,
          ReportState.FIX_VERIFIED,
          ReportState.AWAITING_FIX,
          ReportState.RETEST_REQUESTED,
          "retest_requested",
          "retest requested"
        ].includes(report.state)
      ) {
        actions.push({ key: "closed", label: "Close" });
      }
      const isAwaitingFixOrRetestState =
        report.state === ReportState.AWAITING_FIX ||
        report.state === ReportState.RETEST_REQUESTED ||
        (report.state as any) === "retest_requested" ||
        (report.state as any) === "retest requested";

      if (isAwaitingFixOrRetestState) {
        actions.push({
          key: "retest_requested",
          label: "Request a retest",
          isRetestRequest: true
        });
      }
      if (report.state === ReportState.BUSINESS_REQUEST_INFO) {
        actions.push({ key: "approved", label: "Approve" });
        actions.push({ key: "acceptRisk", label: "Accept Risk" });
        actions.push({
          key: "nonActionableIssue",
          label: "Non-Actionable Issue"
        });
      }
      if (report.state === ReportState.BUSINESS_APPROVED) {
        actions.push({ key: "requestFix", label: "Fixing the Vulnerability" });
      }
      if (report.state === ReportState.QA_MODIFIED) {
        actions.push({ key: "approved", label: "Approve" });
        actions.push({ key: "acceptRisk", label: "Accept Risk" });
        actions.push({
          key: "nonActionableIssue",
          label: "Non-Actionable Issue"
        });
      }
      if (report.state === ReportState.BUSINESS_NON_ACTIONABLE_ISSUE) {
        actions.push({ key: "request_info", label: "Request More Info" });
      }

      // Allow archiving/unarchiving directly from the row dropdown
      if (report.isArchived) {
        actions.push({ key: "unarchive", label: "Unarchive" });
      } else {
        actions.push({ key: "archive", label: "Archive" });
      }
    }
    return actions;
  };

  // Handler for status change
  const handleStatusChange = (
    report: CTBReport,
    actionKey: ActionKeyType,
    isRetestRequest?: boolean
  ) => {
    if (isRetestRequest) {
      showConfirmation(
        "Request Retest",
        "Are you sure you want to request a retest for this report?",
        async () => {
          try {
            await retestRequest(report.id);
            toast.success("Retest request submitted successfully");

            // Invalidate RTK Query cache to ensure fresh data
            dispatch(api.util.invalidateTags([
              { type: 'Report', id: report.id },
              'Reports'
            ]));

            // Also call the onRefresh callback as backup
            onRefresh();
          } catch (err) {
            toast.error("Failed to submit retest request. Please try again.");
          }
        }
      );
      return;
    }

    if (actionKey === "archive" || actionKey === "unarchive") {
      const isArchiving = actionKey === "archive";
      const isNotClosed = !isReportClosed(report.state);
      const archiveWarning = isArchiving && isNotClosed
        ? `This report is not in a Closed state. If it is included in a comprehensive pentest report, that report will be marked as having open findings.\n\nYou can always unarchive this report later and take action on it to bring it to a Closed state.`
        : undefined;
      showConfirmation(
        isArchiving ? "Archive Report" : "Unarchive Report",
        `Are you sure you want to ${isArchiving ? "archive" : "unarchive"} this report?`,
        async () => {
          try {
            await bulkArchiveReports({ reportIds: [report.id], archive: isArchiving }).unwrap();
            toast.success(`Report successfully ${isArchiving ? 'archived' : 'unarchived'}`);
            dispatch(api.util.invalidateTags([{ type: 'Report', id: report.id }, 'Reports']));
            onRefresh();
          } catch (e) {
            toast.error(`Failed to ${isArchiving ? "archive" : "unarchive"} report`);
          }
        },
        undefined,
        archiveWarning
      );
      return;
    }

    let state: ReportStateType | undefined;
    let rejectReason: ReportRejectReason | undefined;

    switch (actionKey) {
      case "approved":
        state = "approved";
        break;
      case "rejected":
        state = "rejected";
        rejectReason = ReportRejectReason.INFORMATIVE;
        break;
      case "closed":
        state = "closed";
        rejectReason = ReportRejectReason.INFORMATIVE;
        break;
      case "request_info":
        state = "request info";
        break;
      case "acceptRisk":
        state = "acceptRisk";
        break;
      case "nonActionableIssue":
        state = "nonActionableIssue";
        break;
      case "requestFix":
        state = "requestFix";
        break;
      default:
        return;
    }

    if (!state) return;

    const infoKey =
      actionKey === "approved"
        ? "APPROVE"
        : actionKey === "rejected"
          ? "REJECT"
          : actionKey === "closed"
            ? "CLOSE"
            : actionKey === "request_info"
              ? "REQUEST_INFO"
              : actionKey === "acceptRisk"
                ? "ACCEPT_RISK"
                : actionKey === "nonActionableIssue"
                  ? "NON_ACTIONABLE"
                  : actionKey === "requestFix"
                    ? "REQUEST_FIX"
                    : null;

    if (infoKey && REPORT_STATUS_INFO[infoKey]) {
      showConfirmation(
        REPORT_STATUS_INFO[infoKey].title,
        REPORT_STATUS_INFO[infoKey].description,
        async () => {
          try {
            await updateReportState({
              id: report.id,
              state: state as ReportStateType,
              rejectReason
            });
            toast.success("Report status updated");
          } catch (e) {
            toast.error("Failed to update report status");
          }
        },
        REPORT_STATUS_INFO[infoKey].info
      );
    }
  };

  return (
    <div className="min-h-[500px] bg-white rounded-xl border border-gray-200 shadow-sm">
      {/* Search and Filter Bar */}
      {!hideFilters && (
        <div className="px-4 pt-6 pb-2.5">
          <FilterSectionReports
            reports={reports}
            programNames={filterOptions.programNames}
            vulnerabilityTypes={filterOptions.vulnerabilityTypes}
            reportStates={filterOptions.reportStates}
            severityCategories={filterOptions.severityCategories}
            pentesters={filterOptions.pentesters}
            filters={filters}
            onFilter={onFilter}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
          />
        </div>
      )}

      {/* Compact Premium Bulk Actions - BUSINESS AND ADMIN ROLES ONLY */}
      {role && [UserRole.BUSINESS, UserRole.BUSINESS_MANAGER, UserRole.BUSINESS_ADMINISTRATOR, UserRole.DEVELOPER, UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER].includes(role) && (
        <div className="px-4 py-2">
          {/* Compact Toggle Bar */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200">
            <div className="flex items-center justify-between px-4 py-2.5">
              {/* Left Section - Icon and Title */}
              <div className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-md flex items-center justify-center transition-all duration-200 ${bulkActionsEnabled
                  ? 'text-gray-900'
                  : 'text-gray-500'
                  }`} style={!bulkActionsEnabled ? { backgroundColor: '#F3F4F6' } : {}}>
                  <img src={BulkActIcon} alt="Bulk Actions" className="w-3.5 h-3.5" />
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-medium transition-colors duration-200 ${bulkActionsEnabled ? 'text-gray-900' : 'text-gray-600'
                    }`}>
                    Bulk Actions
                  </span>
                  {bulkActionsEnabled && (
                    <div className="flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="text-xs text-gray-500 font-medium">
                        {totalActionableCount} actionable
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Section - Toggle Switch */}
              <button
                onClick={() => setBulkActionsEnabled(!bulkActionsEnabled)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${bulkActionsEnabled
                  ? 'bg-blue-600 focus:ring-blue-500'
                  : 'bg-gray-300 focus:ring-gray-400'
                  }`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${bulkActionsEnabled ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                />
              </button>
            </div>

            {/* Bulk Actions Bar - Slides down when enabled */}
            <div className={`border-t border-gray-100 transition-all duration-300 ease-in-out ${bulkActionsEnabled ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
              }`}>
              {bulkActionsEnabled && (
                <div className="p-3 bg-gray-50">
                  <BulkActionsBar
                    reports={reports}
                    selectedReports={selectedReports}
                    onSelectionChange={handleBulkSelectionChange}
                    onRefresh={onRefresh}
                    currentFilters={filters}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Table Section */}
      {reports.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <div className="mb-4 rounded-full bg-gray-100 p-4">
            <svg
              className="h-8 w-8 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900">
            No reports found
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Try adjusting your filters or search criteria
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto px-4">
          <table className="w-full table-fixed border-collapse">
            <thead>
              <tr style={{ backgroundColor: '#010D2C', borderTop: 'none' }}>
                {/* Checkbox column for bulk selection - BUSINESS AND ADMIN ROLES ONLY */}
                {role && [UserRole.BUSINESS, UserRole.BUSINESS_MANAGER, UserRole.BUSINESS_ADMINISTRATOR, UserRole.DEVELOPER, UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER].includes(role) && bulkActionsEnabled && (
                  <th className="w-[5%] px-4 py-5 text-center text-xs font-semibold uppercase tracking-wider" style={{ color: '#58CC02' }}>
                    <div className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={selectedReports.length === reports.length && reports.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedReports(reports.map(r => r.id));
                          } else {
                            setSelectedReports([]);
                          }
                        }}
                        className="w-4 h-4 text-blue-600 bg-white border-2 border-blue-300 rounded focus:ring-blue-500 focus:ring-2 transition-all duration-200"
                      />
                    </div>
                  </th>
                )}
                <th className="w-[18%] px-4 py-5 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#58CC02' }}>
                  Report Title
                </th>
                <th className="w-[20%] px-4 py-5 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#58CC02' }}>
                  Program Name
                </th>
                {columnVisibility.assetType && (
                  <th className="w-[12%] px-4 py-5 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#58CC02' }}>
                    Asset Type
                  </th>
                )}
                <th className="w-[12%] px-4 py-5 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#58CC02' }}>
                  Severity
                </th>
                <th className="w-[18%] px-4 py-5 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#58CC02' }}>
                  Status
                </th>
                {role === UserRole.RESEARCHER && (
                  <th className="w-[8%] px-4 py-5 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#58CC02' }}>
                    Read Status
                  </th>
                )}
                {columnVisibility.createdAt && (
                  <th className="w-[10%] px-4 py-5 text-left text-xs font-medium uppercase tracking-wider" style={{ color: '#58CC02' }}>
                    Created At
                  </th>
                )}
                {role !== UserRole.RESEARCHER &&
                  columnVisibility.pentester && (
                    <th className="w-[8%] px-4 py-5 text-center text-xs font-medium uppercase tracking-wider" style={{ color: '#58CC02' }}>
                      Pentester
                    </th>
                  )}
                {role !== UserRole.RESEARCHER &&
                  columnVisibility.action && (
                    <th className="w-[7%] px-4 py-5 text-center text-xs font-medium uppercase tracking-wider" style={{ color: '#58CC02' }}>
                      Actions
                    </th>
                  )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {reports.map((report, index) => (
                <ReportTableRow
                  key={report.id}
                  report={report}
                  onClick={handleReportClick}
                  actions={getAvailableActions(
                    report,
                    role ?? UserRole.RESEARCHER
                  )}
                  onAction={(actionKey, isRetestRequest) =>
                    handleStatusChange(report, actionKey, isRetestRequest)
                  }
                  columnVisibility={columnVisibility}
                  index={index}
                  isSelected={selectedReports.includes(report.id)}
                  onSelectionChange={handleReportSelection}
                  showCheckbox={Boolean(role && [UserRole.BUSINESS, UserRole.BUSINESS_MANAGER, UserRole.BUSINESS_ADMINISTRATOR, UserRole.DEVELOPER, UserRole.ADMIN, UserRole.SUB_ADMIN, UserRole.ADMIN_MANAGER].includes(role))}
                  bulkActionsEnabled={bulkActionsEnabled}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {reports.length > 0 && (
        <div className="px-4 py-4 border-t border-gray-200">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}

      <ConfirmationModal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        description={modalConfig.description}
        info={modalConfig.info}
        warning={modalConfig.warning}
      />
    </div>
  );
};

const ReportTableRow = ({
  report,
  onClick,
  actions = [],
  onAction,
  columnVisibility,
  index,
  isSelected,
  onSelectionChange,
  showCheckbox,
  bulkActionsEnabled
}: {
  report: CTBReport;
  onClick: (report: CTBReport) => void;
  actions?: { key: ActionKeyType; label: string; isRetestRequest?: boolean }[];
  onAction?: (actionKey: ActionKeyType, isRetestRequest?: boolean) => void;
  columnVisibility: ColumnVisibility;
  index: number;
  isSelected: boolean;
  onSelectionChange: (reportId: number, isSelected: boolean) => void;
  showCheckbox: boolean;
  bulkActionsEnabled: boolean;
}) => {
  const { program } = useProgram(report.programId);
  const { role } = useUserCredentials();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const assetType = useMemo(
    () => deriveAssetType(report.scope, program?.targets as ProgramTarget[], report.targetValues),
    [report.scope, program?.targets, report.targetValues]
  );

  // Update dropdown position
  const updateDropdownPosition = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 60;
      const spaceBelow = window.innerHeight - buttonRect.bottom;

      setDropdownPosition({
        top:
          spaceBelow < dropdownHeight
            ? buttonRect.top - dropdownHeight - 8
            : buttonRect.bottom + 8,
        left: buttonRect.left - 192 + buttonRect.width
      });
    }
  };

  // Update position when dropdown opens
  useEffect(() => {
    if (dropdownOpen) {
      updateDropdownPosition();
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }
  }, [dropdownOpen]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
        if (activeDropdownId === report.id) {
          activeDropdownId = null;
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [report.id]);

  // Handle dropdown state
  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeDropdownId !== report.id) {
      if (activeDropdownId !== null) {
        const event = new MouseEvent("mousedown", {
          bubbles: true,
          cancelable: true,
          view: window
        });
        document.dispatchEvent(event);
      }
      activeDropdownId = report.id;
    }
    setDropdownOpen(!dropdownOpen);
  };

  return (
    <tr
      id={`report-${report.id}`}
      onClick={() => onClick(report)}
      className={`group cursor-pointer transition-all duration-300 hover:bg-gray-50 ${
        report.isArchived
          ? [UserRole.BUSINESS, UserRole.BUSINESS_MANAGER, UserRole.BUSINESS_ADMINISTRATOR, UserRole.DEVELOPER].includes(role as UserRole)
            ? "bg-orange-50/60"
            : "opacity-75 bg-gray-50/50"
          : ""
      }`}
      style={{
        animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
        opacity: 0,
        transform: "translateY(10px)"
      }}
    >
      {/* Checkbox column for bulk selection */}
      {showCheckbox && bulkActionsEnabled && (
        <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-center">
            <div className="relative">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => onSelectionChange(report.id, e.target.checked)}
                className="w-4 h-4 text-blue-600 bg-white border-2 border-blue-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer transition-all duration-200"
              />
              {isSelected && (
                <div className="absolute inset-0 bg-blue-600 rounded animate-pulse opacity-20"></div>
              )}
            </div>
          </div>
        </td>
      )}
      <td className="px-4 py-6">
        <div
          className="text-sm font-medium text-gray-900 transition-all duration-300 group-hover:text-blue-600 leading-relaxed"
          style={{
            wordBreak: 'break-word',
            lineHeight: '1.4',
            maxWidth: '100%',
            wordSpacing: '0.1em',
            hyphens: 'auto'
          }}
          title={report.title}
        >
          <RichTextRenderer content={report.title} />
        </div>
      </td>
      <td className="px-4 py-3">
        {program && (
          <div className="flex flex-col gap-1.5 py-1">
            <Link
              to={`/dashboard/programs/${program.id}`}
              className="line-clamp-1 text-sm font-semibold text-gray-800 transition-all duration-300 hover:text-blue-600 hover:underline"
              title={program.title}
              onClick={e => e.stopPropagation()}
            >
              {program.title}
            </Link>
          </div>
        )}
      </td>
      {columnVisibility.assetType && (
        <td className="px-4 py-4">
          <div className="flex justify-start">
            <span
              className="text-sm font-medium"
              style={{ color: getRandomAssetTypeColor(assetType || 'N/A') }}
            >
              {assetType || 'N/A'}
            </span>
          </div>
        </td>
      )}
      <td className="px-4 py-4">
        {(report.severity || report.severityCategory) && (
          <div className="flex justify-start">
            <span
              className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium uppercase tracking-wide ${(report.severity?.category || report.severityCategory) === 'Critical'
                ? 'bg-red-500 text-white'
                : (report.severity?.category || report.severityCategory) === 'High'
                  ? 'bg-orange-500 text-white'
                  : (report.severity?.category || report.severityCategory) === 'Medium'
                    ? 'bg-yellow-500 text-white'
                    : (report.severity?.category || report.severityCategory) === 'Low'
                      ? 'bg-green-500 text-white'
                      : ((report.severity?.category || report.severityCategory)?.toLowerCase() === 'none' || (report.severity?.category || report.severityCategory)?.toLowerCase() === 'info')
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-500 text-white'
                }`}
            >
              {((report.severity?.category || report.severityCategory)?.toLowerCase() === 'none' || (report.severity?.category || report.severityCategory)?.toLowerCase() === 'info') ? 'Info' : (report.severity?.category || report.severityCategory)}
            </span>
          </div>
        )}
      </td>
      <td className="px-4 py-4">
        <div className="flex items-center gap-2">
          <ReportStatusPill
            report={report}
            className="px-2.5 py-1 text-xs font-medium transition-all duration-300 group-hover:scale-105"
            showRetestStatus={true}
          />
          {report.isArchived && ![UserRole.BUSINESS, UserRole.BUSINESS_MANAGER, UserRole.BUSINESS_ADMINISTRATOR, UserRole.DEVELOPER].includes(role as UserRole) && (
            <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wider text-gray-600 border border-gray-200 uppercase shadow-sm">
              Archived
            </span>
          )}
        </div>
      </td>
      {role === UserRole.RESEARCHER && (
        <td className="px-4 py-4">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${report.read
              ? 'bg-green-100 text-green-800 border border-green-200'
              : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
              }`}>
              {report.read ? 'Read' : 'Unread'}
            </span>
          </div>
        </td>
      )}
      {columnVisibility.createdAt && (
        <td className="px-4 py-4">
          {report.submittedDate !== undefined && (
            <p className="text-xs font-medium text-gray-600 transition-all duration-300 group-hover:text-gray-900">
              {report.submittedDate}
            </p>
          )}
        </td>
      )}
      {role !== UserRole.RESEARCHER && columnVisibility.pentester && (
        <td className="px-4 py-4">
          {report.creator && (
            <div className="flex justify-center">
              {report.creator.pfp ? (
                <img
                  src={report.creator.pfp}
                  alt={report.creator.displayName}
                  title={report.creator.displayName}
                  className="h-8 w-8 rounded-full border border-gray-200 object-cover shadow-sm transition-all duration-300 group-hover:scale-110 group-hover:border-blue-200"
                />
              ) : (
                <CgProfile
                  size={32}
                  className="text-gray-400 transition-all duration-300 group-hover:scale-110 group-hover:text-gray-600"
                  title={report.creator.displayName}
                />
              )}
            </div>
          )}
        </td>
      )}
      {role !== UserRole.RESEARCHER && columnVisibility.action && (
        <td className="px-4 py-4">
          {actions.length > 0 && (
            <div className="relative flex justify-center">
              <button
                ref={buttonRef}
                className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-all duration-300 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={toggleDropdown}
              >
                update
                <FiChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-300 ${dropdownOpen ? "rotate-180" : ""
                    }`}
                />
              </button>
              {dropdownOpen &&
                createPortal(
                  <div
                    ref={dropdownRef}
                    className="animate-fade-in fixed z-50 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-black/5"
                    style={{
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`,
                      animation: "fadeIn 0.2s ease-out"
                    }}
                  >
                    {actions.map((action, actionIndex) => (
                      <button
                        key={action.key}
                        className="block w-full px-3 py-2 text-left text-xs font-medium text-gray-700 transition-all duration-200 hover:bg-blue-50 hover:text-blue-900"
                        onClick={e => {
                          e.stopPropagation();
                          setDropdownOpen(false);
                          activeDropdownId = null;
                          onAction &&
                            onAction(action.key, action.isRetestRequest);
                        }}
                        title={action.label}
                        style={{
                          animation: `fadeIn 0.2s ease-out ${actionIndex * 0.05
                            }s both`
                        }}
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>,
                  document.body
                )}
            </div>
          )}
        </td>
      )}
    </tr>
  );
};

// Add this at the end of the file, before the export
const styles = `
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
`;

// Add the styles to the document
const styleSheet = document.createElement("style");
styleSheet.textContent = styles;
document.head.appendChild(styleSheet);

export default ReportTable;
